import { Suspense, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, ScrollRestoration } from 'react-router-dom';

import { lazyWithReload as lazy } from '@dfq/shared/lib/lazyWithReload';
import { PageLoader } from '@dfq/shared/components/PageLoader';
import { ConfiguratorPageSkeleton } from '@dfq/shared/components/ConfiguratorPageSkeleton';
import { RequireAuth } from '@dfq/shared/components/RequireAuth';

import { CustomerLayout } from '@/views/layout/CustomerLayout';
import { PublicLayout } from '@/views/layout/PublicLayout';

import { storefrontRoutePaths } from './paths';

/**
 * SF-INFRA-002 — Storefront app router.
 *
 * Architecture
 * ------------
 * Two top-level branches:
 *
 *   PublicLayout  — NavBar + main + Footer. Wraps every anonymous-accessible
 *                   route (home, catalog, auth, info pages, 403, catch-all
 *                   404).
 *   CustomerLayout — wraps the customer-portal routes (`/account/*` +
 *                    `/checkout/confirmation/*`) with the post-login chrome
 *                    (NavBar + sidebar-aware content area + Footer).
 *                    `RequireAuth` is intentionally NOT layered above this
 *                    branch yet — the scaffold needs the portal browsable
 *                    without a working auth backend. Re-introduce
 *                    `<RequireAuth fallbackPath={...}>` above this element
 *                    once SH-AUTH-005 lands. `/cart` and `/checkout` are
 *                    also public for the same scaffold reason.
 *
 * Every route-level view is loaded via `React.lazy()` so
 * `pnpm --filter storefront build` produces one chunk per view. Each lazy
 * element is wrapped in its own `<Suspense fallback={<PageLoader />}>` so a
 * slow chunk only suspends the current view, never the entire shell.
 */

// ──────────────────────────────────────── Public views ────────────────────────────────────────
const HomeView = lazy(() => import('@/views/public/HomeView'));
const AboutView = lazy(() => import('@/views/public/AboutView'));
const HowToOrderView = lazy(() => import('@/views/public/HowToOrderView'));
const FileRequirementsView = lazy(() => import('@/views/public/FileRequirementsView'));
const WholesaleView = lazy(() => import('@/views/public/WholesaleView'));
const FAQView = lazy(() => import('@/views/public/FAQView'));
const ContactView = lazy(() => import('@/views/public/ContactView'));
const CustomDesignRequestView = lazy(() => import('@/views/public/CustomDesignRequestView'));
// SF-QUOTE-103 — public quote-request flow, usable without an account.
const QuoteRequestView = lazy(() => import('@/views/public/QuoteRequestView'));
const QuoteAccessView = lazy(() => import('@/views/public/QuoteAccessView'));
const TermsView = lazy(() => import('@/views/public/TermsView'));
const PrivacyView = lazy(() => import('@/views/public/PrivacyView'));
const SearchView = lazy(() => import('@/views/public/SearchView'));
const CmsPageView = lazy(() => import('@/views/public/CmsPageView'));

// Catalog
const CategoryView = lazy(() => import('@/views/catalog/CategoryView'));
const CategoryBrowseView = lazy(() => import('@/views/catalog/CategoryBrowseView'));
const CategoryLandingView = lazy(() => import('@/views/catalog/CategoryLandingView'));
const ProductDetailView = lazy(() => import('@/views/catalog/ProductDetailView'));

// SF-CONFIG-003 — Phase 2 product type landing pages
const ProductTypeLandingView = lazy(() => import('@/views/catalog/ProductTypeLandingView'));

// SF-CONFIG-004 — Step-by-step configurator wizard shell
const StepByStepConfigurator = lazy(() => import('@/views/catalog/StepByStepConfigurator'));

// Auth (pre-login)
const LoginView = lazy(() => import('@/views/auth/LoginView'));
const RegisterView = lazy(() => import('@/views/auth/RegisterView'));
const ForgotPasswordView = lazy(() => import('@/views/auth/ForgotPasswordView'));
const ResetPasswordView = lazy(() => import('@/views/auth/ResetPasswordView'));
const RestoreAccountView = lazy(() => import('@/views/auth/RestoreAccountView'));
const VerifyEmailView = lazy(() => import('@/views/auth/VerifyEmailView'));

// System (inside PublicLayout)
const ForbiddenView = lazy(() => import('@/views/system/ForbiddenView'));
const NotFoundView = lazy(() => import('@/views/system/NotFoundView'));

// ──────────────────────────────────────── Customer portal views ────────────────────────────────────────
const AccountOverviewView = lazy(() => import('@/views/account/AccountOverviewView'));
const ProfileView = lazy(() => import('@/views/account/ProfileView'));
const SecurityView = lazy(() => import('@/views/account/SecurityView'));
const OrdersListView = lazy(() => import('@/views/account/Orders'));
const OrderDetailView = lazy(() => import('@/views/account/Orders/OrderDetail'));
const ArtworkView = lazy(() => import('@/views/account/ArtworkView'));
const PaymentMethodsView = lazy(() => import('@/views/account/PaymentMethodsView'));
const AddressesView = lazy(() => import('@/views/account/AddressesView'));
const QuotesView = lazy(() => import('@/views/account/QuotesView'));
const NotificationsView = lazy(() => import('@/views/account/NotificationsView'));
const QuoteDetailView = lazy(() => import('@/views/account/QuoteDetailView'));

// SF-PROOFLOOP-010 — Proof route dispatcher (login-gated, deep-linked from
// proof-ready email). Coexists with the contour proof flow — see the
// container's own docblock for the 404-probe branching logic.
const ProofRouterView = lazy(() => import('@/views/account/ProofRouterView'));

// SF-SHAPED-003 dev-only smoke test — tree-shaken out of production builds.
const ShapeEditorSmokeTest = import.meta.env.DEV ? lazy(() => import('@/views/dev/ShapeEditorSmokeTest')) : null;

const CartView = lazy(() => import('@/views/checkout/CartView'));
// SF-FULFILLMENT-004: CheckoutView is no longer mounted directly — the gate
// wraps it and decides authenticated vs. anonymous rendering.
const CheckoutAccountGate = lazy(() =>
  import('@/views/checkout/containers/CheckoutAccountGate').then((m) => ({
    default: m.CheckoutAccountGate,
  })),
);
const CheckoutConfirmationView = lazy(() => import('@/views/checkout/CheckoutConfirmationView'));
const PayBalanceView = lazy(() => import('@/views/account/Orders/PayBalanceView'));

/**
 * Each lazy view renders inside its own Suspense boundary so a slow chunk only
 * suspends the current view (PublicLayout / CustomerLayout stays mounted with
 * a PageLoader in the content area).
 */
function lazyElement(Component: ComponentType): ReactElement {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

/** Wrap the lazy CmsPageView with a concrete slug prop. */
function cmsPageElement(slug: string): ReactElement {
  return (
    <Suspense fallback={<PageLoader />}>
      <CmsPageView slug={slug} />
    </Suspense>
  );
}

/** New pages open at the top; Back/Forward restore where the reader was. */
function RootRoute() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootRoute />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { path: storefrontRoutePaths.home, element: lazyElement(HomeView) },

          // Catalog
          // `/products` — top-level category landing (from cascade)
          { path: '/products', element: lazyElement(CategoryLandingView) },

          // SF-CONFIG-003 — Phase 2 product type landing. Namespaced under
          // /product-types/ to avoid a route-score collision with categoryPattern
          // (/products/:categorySlug). The configure sub-route placeholder keeps the
          // path structure consistent for SF-CONFIG-004 which mounts the wizard at
          // /product-types/:type-slug/configure.
          {
            path: storefrontRoutePaths.configurator.productTypeLandingPattern,
            element: lazyElement(ProductTypeLandingView),
          },

          // SF-CONFIG-004 — Step-by-step configurator wizard. Mounted at
          // /product-types/:type-slug/configure, more specific than the landing
          // pattern so React Router scores it first.
          {
            path: storefrontRoutePaths.configurator.productTypeConfigurePattern,
            element: (
              <Suspense fallback={<ConfiguratorPageSkeleton />}>
                <StepByStepConfigurator />
              </Suspense>
            ),
          },

          { path: storefrontRoutePaths.catalog.categoryPattern, element: lazyElement(CategoryView) },
          {
            path: storefrontRoutePaths.catalog.productPattern,
            element: lazyElement(ProductDetailView),
          },
          // SF-NAV-001 — taxonomy-driven category browse. The `*` splat captures
          // multi-segment paths (e.g. `vehicle-wraps/cars`) so the view can
          // resolve them via the path endpoint without nested route configuration.
          {
            path: storefrontRoutePaths.catalog.categoryBrowsePattern,
            element: lazyElement(CategoryBrowseView),
          },

          // Info pages
          { path: storefrontRoutePaths.about, element: lazyElement(AboutView) },
          { path: storefrontRoutePaths.howItWorks, element: lazyElement(HowToOrderView) },
          // Redirect the old /how-to-order path so existing links and bookmarks resolve.
          { path: '/how-to-order', element: <Navigate to={storefrontRoutePaths.howItWorks} replace /> },
          { path: storefrontRoutePaths.fileRequirements, element: lazyElement(FileRequirementsView) },
          { path: storefrontRoutePaths.wholesale, element: lazyElement(WholesaleView) },
          { path: storefrontRoutePaths.faq, element: lazyElement(FAQView) },
          { path: storefrontRoutePaths.contact, element: lazyElement(ContactView) },
          {
            path: storefrontRoutePaths.customDesign,
            element: lazyElement(CustomDesignRequestView),
          },
          // SF-QUOTE-103 — public quote-request page + guest access-token view.
          { path: storefrontRoutePaths.quote, element: lazyElement(QuoteRequestView) },
          {
            path: storefrontRoutePaths.quoteAccessPattern,
            element: lazyElement(QuoteAccessView),
          },
          { path: storefrontRoutePaths.terms, element: lazyElement(TermsView) },
          { path: storefrontRoutePaths.privacy, element: lazyElement(PrivacyView) },
          { path: storefrontRoutePaths.search, element: lazyElement(SearchView) },

          // CMS-driven pages not covered by dedicated view files
          { path: '/shipping', element: cmsPageElement('shipping') },
          { path: '/returns', element: cmsPageElement('returns') },
          { path: '/installer-program', element: cmsPageElement('installer-program') },
          // SF-DSVC-103 — "Designs" top-bar item. CMS-managed so the client can edit
          // the copy; its CTA links into the existing custom-design request form.
          { path: storefrontRoutePaths.designServices, element: cmsPageElement('design-services') },

          // SF-SHAPED-003 dev-only smoke test — only registered in dev; tree-shaken in prod.
          ...(import.meta.env.DEV && ShapeEditorSmokeTest
            ? [{ path: '/__dev/shape-editor-smoke', element: lazyElement(ShapeEditorSmokeTest) }]
            : []),

          // /sign-in → /login alias (header "Sign in" button uses /login via callback,
          // but direct navigations to /sign-in should resolve cleanly)
          { path: '/sign-in', element: <Navigate to={storefrontRoutePaths.auth.login} replace /> },

          // Cart + Checkout.
          // SF-FULFILLMENT-004: /checkout is now gated — anonymous users see a
          // sign-in / create-account choice; authenticated users see CheckoutView.
          { path: storefrontRoutePaths.cart, element: lazyElement(CartView) },
          { path: storefrontRoutePaths.checkout.root, element: lazyElement(CheckoutAccountGate) },

          // Retired re-authorization link (SF-PAYMENTS-003). Emails carrying it are
          // still in customers' inboxes; the token it names can no longer be
          // resolved, so the order list is as close as we can land them.
          //
          // Deliberately outside RequireAuth. A redirect needs no session, and
          // behind the guard it raced the boot silent-refresh — a signed-in
          // customer following the link could be bounced to /login instead. The
          // order list applies its own guard on arrival.
          {
            path: storefrontRoutePaths.checkout.reauthorizePattern,
            element: <Navigate to={storefrontRoutePaths.account.orders} replace />,
          },

          // Auth (pre-login)
          { path: storefrontRoutePaths.auth.login, element: lazyElement(LoginView) },
          { path: storefrontRoutePaths.auth.register, element: lazyElement(RegisterView) },
          {
            path: storefrontRoutePaths.auth.forgotPassword,
            element: lazyElement(ForgotPasswordView),
          },
          {
            path: storefrontRoutePaths.auth.resetPasswordPattern,
            element: lazyElement(ResetPasswordView),
          },
          { path: storefrontRoutePaths.auth.verifyEmail, element: lazyElement(VerifyEmailView) },
          {
            path: storefrontRoutePaths.auth.restoreAccountPattern,
            element: lazyElement(RestoreAccountView),
          },

          // System
          { path: storefrontRoutePaths.forbidden, element: lazyElement(ForbiddenView) },

          // Catch-all — must be last child so specific paths win first.
          { path: '*', element: lazyElement(NotFoundView) },
        ],
      },
      {
        // Customer portal — `CustomerLayout` chrome wrapped in `RequireAuth` so
        // unauthenticated visits to `/account/*` redirect to `/login?returnUrl=…`
        // (per SF-ACCT-001 acceptance criteria). The `useReturnUrlParam` option
        // emits the query-string shape the storefront login view consumes.
        element: <RequireAuth fallbackPath="/login" useReturnUrlParam />,
        children: [
          {
            element: <CustomerLayout />,
            children: [
              // /account + /account/*
              { path: storefrontRoutePaths.account.root, element: lazyElement(AccountOverviewView) },
              { path: storefrontRoutePaths.account.profile, element: lazyElement(ProfileView) },
              { path: storefrontRoutePaths.account.security, element: lazyElement(SecurityView) },
              { path: storefrontRoutePaths.account.orders, element: lazyElement(OrdersListView) },
              {
                // SF-PROOFLOOP-010 — more specific than orderDetailPattern (3 segments vs 2)
                // so React Router ranks it first. Registered explicitly before the 2-segment
                // orderDetailPattern to make the priority visually obvious.
                path: storefrontRoutePaths.account.proofPattern,
                element: lazyElement(ProofRouterView),
              },
              {
                // More specific than orderDetailPattern, so it is registered first
                // for the same reason proofPattern is.
                path: storefrontRoutePaths.account.payBalancePattern,
                element: lazyElement(PayBalanceView),
              },
              {
                path: storefrontRoutePaths.account.orderDetailPattern,
                element: lazyElement(OrderDetailView),
              },
              { path: storefrontRoutePaths.account.artwork, element: lazyElement(ArtworkView) },
              {
                path: storefrontRoutePaths.account.paymentMethods,
                element: lazyElement(PaymentMethodsView),
              },
              {
                path: storefrontRoutePaths.account.addresses,
                element: lazyElement(AddressesView),
              },
              { path: storefrontRoutePaths.account.quotes, element: lazyElement(QuotesView) },
              {
                path: storefrontRoutePaths.account.notifications,
                element: lazyElement(NotificationsView),
              },
              // SF-QUOTE-103: the account-portal "new quote" form moved to the public
              // /quote page (usable without an account); this redirect keeps old
              // bookmarks/emails working. More specific than the list route,
              // registered first so router scores it higher.
              {
                path: storefrontRoutePaths.account.quoteNew,
                element: <Navigate to={storefrontRoutePaths.quote} replace />,
              },
              { path: storefrontRoutePaths.account.quoteDetailPattern, element: lazyElement(QuoteDetailView) },

              // Checkout confirmation — also under CustomerLayout chrome for now.
              {
                path: storefrontRoutePaths.checkout.confirmationPattern,
                element: lazyElement(CheckoutConfirmationView),
              },
            ],
          },
        ],
      },
    ],
  },
]);

/**
 * Tiny convenience wrapper so `App.tsx` stays free of router-plumbing noise.
 * Exports ONLY a component so `react-refresh/only-export-components` stays
 * happy — consumers that need the route-path helpers import them from
 * `@router/paths` directly.
 */
export function StorefrontRouterProvider() {
  return <RouterProvider router={router} />;
}
